import React from "react";
import { useRevelado } from '@/shared/hooks/useRevelado';
import { useContador } from '@/shared/hooks/useContador';

interface KpiCardProps {
  title: string;
  value: string | number;
  subtext?: string;
  /** Texto pegado a la cifra animada, como `%` o ` días`. */
  suffix?: string;
  trend?: {
    value: number;
    isPositive: boolean;
    period: string;
  };
  icon?: React.ReactNode;
  variant?:
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "error"
    | "info"
    | "purple";
  isLoading?: boolean;
}

const variantClasses = {
  primary: {
    bg: "bg-primary/5",
    iconBg: "bg-primary/10",
    text: "text-primary",
    icon: "text-primary",
  },
  secondary: {
    bg: "bg-secondary/5",
    iconBg: "bg-secondary/10",
    text: "text-secondary",
    icon: "text-secondary",
  },
  success: {
    bg: "bg-success/5",
    iconBg: "bg-success/10",
    text: "text-success",
    icon: "text-success",
  },
  warning: {
    bg: "bg-warning/5",
    iconBg: "bg-warning/10",
    text: "text-warning",
    icon: "text-warning",
  },
  error: {
    bg: "bg-error/5",
    iconBg: "bg-error/10",
    text: "text-error",
    icon: "text-error",
  },
  info: {
    bg: "bg-info/5",
    iconBg: "bg-info/10",
    text: "text-info",
    icon: "text-info",
  },
  purple: {
    bg: "bg-purple/5",
    iconBg: "bg-purple/10",
    text: "text-purple",
    icon: "text-purple",
  },
};

const KpiCard: React.FC<KpiCardProps> = ({
  title,
  value,
  subtext,
  suffix = "",
  trend,
  icon,
  variant = "primary",
  isLoading = false,
}) => {
  const classes = variantClasses[variant];
  // A-06: la tarjeta se revela al asomarse, no aparece de golpe con la carga.
  const revelado = useRevelado<HTMLDivElement>();
  // A-22: la cifra sube desde cero cuando la tarjeta entra en pantalla.
  const contador = useContador<HTMLParagraphElement>(
    typeof value === "number" ? value : 0,
  );

  if (isLoading) {
    return (
      <div
        className="p-6 rounded-xl shadow-sm border border-border"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div className="animate-pulse">
          <div className="flex items-start justify-between mb-6">
            <div className="h-4 bg-neutral/20 rounded w-2/3"></div>
            <div className="h-12 w-12 bg-neutral/20 rounded-lg"></div>
          </div>
          <div className="h-10 bg-neutral/20 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={revelado.ref}
      className={`p-6 rounded-xl shadow-sm border border-border hover:shadow-md ${revelado.className}`}
      style={{ backgroundColor: "#FFFFFF", ...revelado.style }}
    >
      {/* Header con título e ícono */}
      <div className="flex items-start justify-between mb-6">
        <h3 className="text-sm font-medium text-content-muted">{title}</h3>
        {icon && (
          <div
            className={`p-3 ${classes.iconBg} rounded-lg mt-[-10px] mr-[-10px] flex items-center justify-center`}
          >
            <div
              className={`${classes.icon} w-5! h-5! flex items-center justify-center`}
            >
              {icon}
            </div>
          </div>
        )}
      </div>

      {/* Valor principal */}
      <div className="mb-1">
        <p ref={contador.ref} className="text-5xl font-bold text-content">
          {typeof value === "number"
            ? `${contador.valor.toLocaleString("es-MX")}${suffix}`
            : value}
        </p>
      </div>

      {/* Subtext o trend */}
      {(subtext || trend) && (
        <div className="mt-2">
          {subtext && <p className="text-sm text-content-muted">{subtext}</p>}
          {trend && (
            <div className="flex items-center space-x-1">
              <span
                className={`text-xs font-medium ${trend.isPositive ? "text-success" : "text-error"}`}
              >
                {trend.isPositive ? "↗" : "↘"} {Math.abs(trend.value)}%
              </span>
              <span className="text-xs text-content-muted">{trend.period}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KpiCard;
